/**
 * Using for recommendation products and recent view products
 */
if (!customElements.get('product-slider')) {
    customElements.define(
        'product-slider',
        class ProductSlider extends HTMLElement {
            constructor() {
                super();
            }

            connectedCallback() {
                this.selectors = {
                    productsWrap: '.products-grid-wrap',
                    products: '.products-grid',
                    swiperControls: '.swiper-controls',
                };
                this.classes = {
                    grid: 'f-grid',
                    swiper: 'swiper',
                    swiperWrapper: 'swiper-wrapper',
                };

                this.sectionId = this.dataset.sectionId;
                this.elements = FoxTheme.utils.queryDomNodes(this.selectors, this);
                this.elements.section = this.closest(`.section-${this.sectionId}`);

                this.enableSlider = this.dataset.enableSlider === 'true';
                this.enableSwipeMobile = this.hasAttribute('data-mobile-items');
                this.items = parseInt(this.dataset.items);
                this.tabletItems = parseInt(this.dataset.tabletItems);

                this.sliderInstance = false;
                this._autoSlideTimer = null;
                this._stopAutoSlide = () => {
                    if (this._autoSlideTimer) {
                        clearInterval(this._autoSlideTimer);
                        this._autoSlideTimer = null;
                    }
                };

                // Exit early only if NEITHER desktop slider NOR mobile swipe is enabled
                if (!this.enableSlider && !this.enableSwipeMobile) return;

                if (!this.elements.productsWrap || !this.elements.products) return;

                const mql = window.matchMedia(FoxTheme.config.mediaQueryMobile || '(max-width: 767px)');
                mql.onchange = this.init.bind(this);
                this.init();

                // If the grid is initially empty (e.g. recently viewed products fetched via AJAX),
                // observe for DOM injections and re-initialize once loaded.
                // We do NOT call init() now (grid is empty — Swiper would measure 0px widths).
                if (this.elements.products && this.elements.products.children.length === 0) {
                    this.observer = new MutationObserver(() => {
                        if (this.elements.products.children.length > 0) {
                            if (this.observer) this.observer.disconnect();
                            // Wait for the browser to fully lay out the injected HTML
                            // before Swiper calculates slide widths.
                            requestAnimationFrame(() => {
                                setTimeout(() => this.init(), 100);
                            });
                        }
                    });
                    this.observer.observe(this.elements.products, { childList: true });
                    // Don't call init() yet — wait for products to arrive
                    return;
                }
            }

            init() {
                this.destroySlider();

                if (this.shouldDestroySlider()) return;

                const isMobile = window.matchMedia(FoxTheme.config.mediaQueryMobile || '(max-width: 767px)').matches;

                // On mobile: use Swiper if mobile swipe is enabled, otherwise use native CSS swipe
                // On desktop: use Swiper if desktop slider is enabled
                if (isMobile) {
                    if (this.enableSwipeMobile) {
                        this.initSlider();
                    } else {
                        this.fixMobileWidths();
                    }
                } else {
                    if (this.enableSlider) {
                        this.initSlider();
                    }
                }
            }

            initSlider() {
                if (typeof this.sliderInstance === 'object') return;

                const columnGap = FoxTheme.utils.getGridColumnGap(this.elements.products);

                const actualItemCount = this.elements.products.children.length;

                // Don't initialize with an empty grid — Swiper will measure 0px widths
                // and auto-slide / loop will silently fail.
                if (actualItemCount === 0) return;
                const desktopItems = parseInt(this.items) || 4;
                const mobileItems = parseInt(this.dataset.mobileItems) || 1;

                const isMobile = window.matchMedia(FoxTheme.config.mediaQueryMobile || '(max-width: 767px)').matches;

                // Loop: only enable if the setting is explicitly 'true' and there are enough products.
                // Use >= so that e.g. 2 products + 2 columns still enables loop (Swiper clones slides).
                const canLoopDesktop = this.dataset.loopDesktop === 'true' && actualItemCount >= desktopItems;
                const canLoopMobile = this.dataset.loopMobile === 'true' && actualItemCount >= mobileItems;
                const currentLoop = isMobile ? canLoopMobile : canLoopDesktop;

                // Auto-slide: needs at least as many products as there are columns visible.
                // Use >= so that 2-column layout with 2 products still auto-slides.
                const isAutoSlide = isMobile
                    ? (this.dataset.autoSlide === 'true' && actualItemCount >= mobileItems)
                    : (this.dataset.autoSlideDesktop === 'true' && actualItemCount >= desktopItems);

                const speedDesktop = (parseInt(this.dataset.autoSlideDesktopSpeed) || 3) * 1000;
                const speedMobile = (parseInt(this.dataset.autoSlideSpeed) || 3) * 1000;
                const autoplayDelay = isMobile ? speedMobile : speedDesktop;

                this.sliderOptions = {
                    slidesPerView: mobileItems,
                    spaceBetween: columnGap.mobile || 15,
                    navigation: {
                        nextEl: this.elements.section ? this.elements.section.querySelector('.swiper-button-next') : null,
                        prevEl: this.elements.section ? this.elements.section.querySelector('.swiper-button-prev') : null,
                    },
                    breakpoints: {
                        768: {
                            slidesPerView: this.tabletItems > 3 ? 3 : (parseInt(this.tabletItems) || 3),
                            spaceBetween: columnGap.tablet,
                        },
                        1024: {
                            slidesPerView: desktopItems > 4 ? 4 : desktopItems,
                            spaceBetween: columnGap.tabletLarge,
                        },
                        1280: {
                            slidesPerView: desktopItems,
                            spaceBetween: columnGap.desktop,
                        },
                    },
                    loop: currentLoop,
                    threshold: 2,
                };

                // Strip native CSS swipe classes so Swiper can take full control on mobile
                this.elements.productsWrap.classList.remove('swipe-mobile', `swipe-mobile--${mobileItems}-cols`);
                this.elements.products.classList.remove('swipe-mobile__inner');

                this.elements.productsWrap.classList.add(this.classes.swiper);
                this.elements.products.classList.remove(this.classes.grid);
                this.elements.products.classList.add(this.classes.swiperWrapper);

                // swiper-controls may not exist (e.g. mobile-only mode with no desktop nav rendered)
                if (this.elements.swiperControls) {
                    this.elements.swiperControls.classList.add('md:block');
                }

                this.sliderInstance = new window.FoxTheme.Carousel(this.elements.productsWrap, this.sliderOptions);
                this.sliderInstance.init();

                // Manual autoplay via setInterval — reliable regardless of FoxTheme.Carousel internals
                // First, always clear any previous interval
                this._stopAutoSlide();

                if (isAutoSlide) {
                    this._autoSlideActive = true;

                    this._startAutoSlide = () => {
                        this._stopAutoSlide();
                        this._autoSlideTimer = setInterval(() => {
                            const swiper = this.sliderInstance && this.sliderInstance.slider;
                            if (!swiper) return;
                            // Stop at the end unless looping
                            if (swiper.isEnd && !currentLoop) {
                                this._stopAutoSlide();
                            } else {
                                swiper.slideNext();
                            }
                        }, autoplayDelay);
                    };

                    this._stopAutoSlide = () => {
                        if (this._autoSlideTimer) {
                            clearInterval(this._autoSlideTimer);
                            this._autoSlideTimer = null;
                        }
                    };

                    this._startAutoSlide();

                    // Pause on hover (desktop) and on touch (mobile)
                    this._boundStop  = () => this._stopAutoSlide();
                    this._boundStart = () => this._startAutoSlide();
                    this.addEventListener('mouseenter', this._boundStop);
                    this.addEventListener('mouseleave', this._boundStart);
                    this.addEventListener('touchstart', this._boundStop, { passive: true });
                    this.addEventListener('touchend',   this._boundStart, { passive: true });
                }

                this.handleAccessibility();
                this.fixQuickviewDuplicate();
                this.calcNavButtonsPosition();

                window.addEventListener(
                    'resize',
                    FoxTheme.utils.debounce(() => {
                        this.calcNavButtonsPosition();
                    }, 100),
                );
            }

            handleAccessibility() {
                const focusableElements = FoxTheme.a11y.getFocusableElements(this);

                focusableElements.forEach((element) => {
                    element.addEventListener('focusin', (event) => {
                        if (event.relatedTarget !== null) {
                            if (element.closest('.swiper-slide')) {
                                const slide = element.closest('.swiper-slide');
                                this.sliderInstance.slider.slideTo(this.sliderInstance.slider.slides.indexOf(slide));
                            }
                        } else {
                            element.blur();
                        }
                    });
                });
            }

            destroySlider() {
                // Stop manual autoplay and remove listeners
                if (this._autoSlideTimer) {
                    clearInterval(this._autoSlideTimer);
                    this._autoSlideTimer = null;
                }
                if (this._boundStop) {
                    this.removeEventListener('mouseenter', this._boundStop);
                    this.removeEventListener('mouseleave', this._boundStart);
                    this.removeEventListener('touchstart', this._boundStop);
                    this.removeEventListener('touchend',   this._boundStart);
                    this._boundStop = null;
                    this._boundStart = null;
                }
                this._autoSlideActive = false;

                if (this.elements.productsWrap) {
                    this.elements.productsWrap.classList.remove(this.classes.swiper);
                }
                if (this.elements.products) {
                    this.elements.products.classList.remove(this.classes.swiperWrapper);
                    this.elements.products.classList.add(this.classes.grid);
                }
                if (this.elements.swiperControls) {
                    this.elements.swiperControls.classList.remove('md:block');
                }

                // Restore native mobile classes in case we are reverting to native CSS swipe
                const isMobile = window.matchMedia(FoxTheme.config.mediaQueryMobile || '(max-width: 767px)').matches;
                if (isMobile && this.dataset.mobileItems && this.elements.productsWrap && this.elements.products) {
                    this.elements.productsWrap.classList.add('swipe-mobile', `swipe-mobile--${this.dataset.mobileItems}-cols`);
                    this.elements.products.classList.add('swipe-mobile__inner');
                }

                if (typeof this.sliderInstance !== 'object') return;
                try {
                    // Guard against broken 0-slide instances that may throw on destroy
                    this.sliderInstance.slider.destroy();
                } catch (e) {
                    // Swiper was in a broken state — safe to ignore
                }
                this.sliderInstance = false;
            }

            fixMobileWidths() {
                const container = this.querySelector('.swipe-mobile');
                const wrapper = this.querySelector('.swipe-mobile__inner');
                if (!container || !wrapper) return;

                const mobileItems = parseInt(this.dataset.mobileItems) || 1;

                const updateWidth = () => {
                    const isMobile = window.matchMedia(FoxTheme.config.mediaQueryMobile || '(max-width: 767px)').matches;
                    if (!isMobile) {
                        wrapper.style.removeProperty('grid-auto-columns');
                        return;
                    }
                    // Container's exact screen width minus page padding (15px each side)
                    const availableWidth = window.innerWidth - 30;

                    // Gap between items
                    const gap = parseFloat(window.getComputedStyle(wrapper).columnGap)
                        || parseFloat(window.getComputedStyle(wrapper).gap)
                        || 15;

                    // Calculate exact width for each item to perfectly fill the screen
                    const exactWidth = (availableWidth - (gap * (mobileItems - 1))) / mobileItems;

                    wrapper.style.setProperty('grid-auto-columns', exactWidth + 'px', 'important');
                    wrapper.style.setProperty('grid-template-columns', 'none', 'important');
                    wrapper.style.setProperty('grid-auto-flow', 'column', 'important');
                };

                updateWidth();
                if (!this.mobileWidthBound) {
                    window.addEventListener('resize', FoxTheme.utils.debounce(updateWidth, 100));
                    this.mobileWidthBound = true;
                }
            }

            initMobileAutoSlide() {
                // Deprecated: Entirely replaced by Swiper initialization on mobile
            }

            destroyMobileAutoSlide() {
                // Deprecated
            }

            calcNavButtonsPosition() {
                if (this.dataset.calcButtonPosition !== 'true') return;

                const firstMedia = this.querySelector('.product-card__image-wrapper');
                if (firstMedia && firstMedia.clientHeight > 0 && this.elements.section) {
                    this.elements.section.style.setProperty(
                        '--swiper-navigation-top-offset',
                        parseInt(firstMedia.clientHeight) / 2 + 'px',
                    );
                }
            }

            fixQuickviewDuplicate() {
                let modalIds = [];
                Array.from(this.querySelectorAll('quick-view-modal')).forEach((modal) => {
                    const modalID = modal.getAttribute('id');
                    if (modalIds.includes(modalID)) {
                        modal.remove();
                    } else {
                        modalIds.push(modalID);
                    }
                });
            }

            shouldDestroySlider() {
                const recentlyViewedSection = this.closest('recently-viewed-products');

                if (recentlyViewedSection) {
                    const items = JSON.parse(window.localStorage.getItem('sleektheme:recently-viewed') || '[]');
                    const productId = parseInt(recentlyViewedSection.dataset.productId);

                    // Exclude the current product page from the count
                    if (items.includes(productId)) {
                        items.splice(items.indexOf(productId), 1);
                    }

                    // Use actual DOM count as the source of truth when products have loaded.
                    // Fall back to localStorage count before AJAX load.
                    const actualDomCount = this.elements.products
                        ? this.elements.products.children.length
                        : 0;
                    const itemCount = actualDomCount > 0 ? actualDomCount : items.length;

                    // Need at least more than 1 mobile column to warrant a slider
                    const mobileItems = parseInt(this.dataset.mobileItems) || 1;
                    const desktopItems = parseInt(this.dataset.items) || 4;
                    const isMobile = window.matchMedia(FoxTheme.config.mediaQueryMobile || '(max-width: 767px)').matches;
                    const minRequired = isMobile ? mobileItems : desktopItems;

                    // Destroy only when there are FEWER products than columns on screen.
                    // With equal counts (e.g. 2 products, 2 columns) loop + auto-slide still work.
                    if (itemCount < minRequired) {
                        return true;
                    }
                }

                return false;
            }
        },
    );
}
